import { useCallback, useEffect, useState } from "react";

export interface DebouncedState<T> {
  value: T;
  debouncedValue: T;
  setValue: (next: T) => void;
  setValueImmediate: (next: T) => void;
}

export function useDebouncedState<T>(initialValue: T, delayMs: number): DebouncedState<T> {
  const [value, setValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedValue(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);

  // Skip the delay: set the live and debounced values together so a programmatic change does not
  // schedule a trailing update (e.g. avoids firing a search for a value the user never typed).
  const setValueImmediate = useCallback((next: T) => {
    setValue(next);
    setDebouncedValue(next);
  }, []);

  return { value, debouncedValue, setValue, setValueImmediate };
}
